import { createContext, useContext, useEffect, useMemo, useState } from "react";
import type { AuthSuccessResponse, AuthUser } from "../shared/auth";
import { fetchSession, logoutSession } from "../lib/auth-api";
import {
  clearAuthToken,
  getAuthToken,
  setAuthToken,
} from "../lib/auth-storage";
import { AccessScreen } from "./AccessScreen";

interface AuthContextValue {
  user: AuthUser;
  logout: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [checking, setChecking] = useState(true);

  useEffect(() => {
    const token = getAuthToken();
    if (!token) {
      setChecking(false);
      return;
    }
    fetchSession()
      .then((session) => setUser(session.user))
      .catch(() => clearAuthToken())
      .finally(() => setChecking(false));
  }, []);

  function acceptSession(session: AuthSuccessResponse) {
    setAuthToken(session.token);
    setUser(session.user);
    window.history.replaceState({}, "", window.location.pathname);
  }

  async function logout() {
    try {
      await logoutSession();
    } finally {
      clearAuthToken();
      setUser(null);
    }
  }

  const value = useMemo(
    () => (user ? { user, logout } : null),
    [user],
  );

  if (checking) {
    return (
      <div className="access-shell">
        <div className="access-loading" />
      </div>
    );
  }

  if (!user || !value) {
    return <AccessScreen onAuthenticated={acceptSession} />;
  }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const value = useContext(AuthContext);
  if (!value) {
    throw new Error("useAuth must be used inside AuthProvider");
  }
  return value;
}
